import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/design-system/button';
import { Paginator } from '@/design-system/pagination';
import { Tabs, TabsList, TabsTrigger } from '@/design-system/tabs';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';
import {
  Dispatch,
  DispatchListParams,
  DispatchStatus,
  dispatchesAPI,
} from '@/services/api/dispatches';
import { useUrlParam, useUrlNumber, useUrlPatch } from '@/hooks/urlState';
import { formatDateTime } from '@/utils';

import {
  resolvePresetRange,
  type DatePreset,
} from '../shipments/components/ShipmentFilterPanel';
import CreateDispatchModal from './components/CreateDispatchModal';
import DispatchFilterPanel, {
  countActiveDispatchFilters,
  DispatchFilterValue,
  EMPTY_DISPATCH_FILTERS,
} from './components/DispatchFilterPanel';
import ExportDispatchesModal from './components/ExportDispatchesModal';
import {
  AutoDispatchBadge,
  DispatchStatusBadge,
} from './components/DispatchBadges';

type TabKey = 'ALL' | DispatchStatus;

const TABS: { key: TabKey; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'PREVIEW', label: 'Preview' },
  { key: 'COMMITTED', label: 'Committed' },
  { key: 'PARTIAL', label: 'Partial' },
  { key: 'FAILED', label: 'Failed' },
  { key: 'CANCELLED', label: 'Cancelled' },
];

const TAB_KEYS = TABS.map((t) => t.key) as readonly TabKey[];

const DEFAULT_PAGE_SIZE = 20;

const DATE_PRESET_KEYS: readonly DatePreset[] = [
  'today',
  'yesterday',
  'last_7_days',
  'last_30_days',
];

const DispatchesPage: React.FC = () => {
  const navigate = useNavigate();
  // URL-driven so tab / filters / page are shareable and back-button aware.
  // Tab switches use push history (Back steps through tabs); filters/pagination
  // use replace. Each filter/tab change drops `page` in the same URL write.
  const [tab, setTab] = useUrlParam<TabKey>('tab', 'ALL', {
    allowed: TAB_KEYS,
    resetPage: true,
    history: 'push',
  });
  // Applied filters live in the URL (same contract as the Shipments page);
  // the draft stays local so the popover can be cancelled.
  const { searchParams, patch: patchUrl } = useUrlPatch();
  const filters: DispatchFilterValue = useMemo(() => {
    const preset = searchParams.get('preset');
    return {
      hubId: searchParams.get('hub') ?? '',
      clientIds: (searchParams.get('client') ?? '')
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean),
      datePreset: DATE_PRESET_KEYS.includes(preset as DatePreset)
        ? (preset as DatePreset)
        : '',
      from: searchParams.get('from') ?? '',
      to: searchParams.get('to') ?? '',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);
  const [filterDraft, setFilterDraft] = useState<DispatchFilterValue>(
    EMPTY_DISPATCH_FILTERS
  );
  const [filterOpen, setFilterOpen] = useState(false);

  const [items, setItems] = useState<Dispatch[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [page, setPage] = useUrlNumber('page', 1, { min: 1 });
  const [pageSize, setPageSize] = useUrlNumber('pageSize', DEFAULT_PAGE_SIZE, {
    min: 1,
    resetPage: true,
  });
  const [total, setTotal] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const fetchList = useCallback(async () => {
    setTableLoading(true);
    const params: DispatchListParams = { page, limit: pageSize };
    if (tab !== 'ALL') params.status = tab;
    if (filters.hubId) params.hubId = filters.hubId;
    const clientIds = filters.clientIds
      .map(Number)
      .filter((n) => Number.isFinite(n) && n > 0);
    if (clientIds.length > 0) params.clientIds = clientIds;
    // Preset wins (its inputs are cleared when a preset is active).
    const presetRange = resolvePresetRange(filters.datePreset);
    if (presetRange) {
      params.from = presetRange.from;
      params.to = presetRange.to;
    } else {
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
    }
    try {
      const res = await dispatchesAPI.list(params);
      setItems(res.data ?? []);
      setTotal(res.pagination?.total ?? 0);
    } catch (err) {
      setItems([]);
      setTotal(0);
      toast.error('Could not load dispatches', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setTableLoading(false);
    }
  }, [tab, filters, page, pageSize]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const codeCls =
    'font-mono text-[12px] font-semibold text-text-strong-950 tabular-nums';
  const dateCls = 'text-[13px] text-text-sub-600 tabular-nums';
  const metaCls = 'text-[11px] text-text-sub-600';
  const numCls = 'text-[13px] tabular-nums text-text-sub-600';

  const columns: TableDataColumn<Dispatch>[] = [
    {
      key: 'code',
      title: 'Code',
      render: (_v, record) => (
        <span className={`${codeCls} text-accent`}>{record.code}</span>
      ),
    },
    {
      key: 'hub',
      title: 'Hub',
      render: (_v, record) =>
        // Everything comes from the snapshot frozen at preview — no pitstop
        // lookup. The CODE is the display value (ops reads codes, keputusan
        // Yogi); snapshots frozen before `code` shipped fall back to the
        // name, and rows older than the column show the bare id until the
        // backfill runs. Hover shows the full name.
        record.hub ? (
          <span className={codeCls} title={record.hub.name}>
            {record.hub.code ?? record.hub.name}
          </span>
        ) : (
          <span className={codeCls}>#{record.hubId}</span>
        ),
    },
    {
      key: 'clients',
      title: 'Client',
      render: (_v, record) => {
        const clients = record.clients ?? [];
        if (clients.length === 0) return <span className={metaCls}>—</span>;
        // Every client, comma-separated (keputusan Yogi: tampilkan semua,
        // bukan +N). The cell wraps rather than truncating.
        return (
          <span className="block max-w-[260px] text-[13px] leading-snug text-text-sub-600">
            {clients.map((c) => c.name).join(', ')}
          </span>
        );
      },
    },
    {
      key: 'status',
      title: 'Status',
      render: (_v, record) => (
        <span className="inline-flex items-center gap-1.5">
          <DispatchStatusBadge status={record.status} />
          {/* Auto = created by the scheduled pitstop tick, not an ops user. */}
          <AutoDispatchBadge triggeredBy={record.triggeredBy} size="sm" />
        </span>
      ),
    },
    {
      key: 'riders',
      title: 'Riders',
      align: 'right',
      render: (_v, record) => (
        <span className={numCls}>{record.riderCount}</span>
      ),
    },
    {
      key: 'batches',
      title: 'Batches',
      align: 'right',
      render: (_v, record) => (
        <div className="text-right">
          <p className={numCls}>{record.totalBatches}</p>
          {record.deferredBatchCount > 0 && (
            <p className={metaCls}>{record.deferredBatchCount} deferred</p>
          )}
        </div>
      ),
    },
    {
      key: 'items',
      title: 'Items',
      align: 'right',
      render: (_v, record) => (
        <div className="text-right">
          <p className={numCls}>{record.totalItems}</p>
          {record.unbatchableCount > 0 && (
            <p className={metaCls}>{record.unbatchableCount} unbatchable</p>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      title: 'Created',
      render: (_v, record) => (
        <span className={dateCls}>{formatDateTime(record.createdAt)}</span>
      ),
    },
    {
      key: 'committedAt',
      title: 'Committed',
      render: (_v, record) => (
        <span className={dateCls}>{formatDateTime(record.committedAt)}</span>
      ),
    },
  ];

  const activeFilterCount = useMemo(
    () => countActiveDispatchFilters(filters),
    [filters]
  );

  // Seed the draft from the applied filters whenever the popover opens.
  const handleFilterOpenChange = (open: boolean) => {
    if (open) setFilterDraft(filters);
    setFilterOpen(open);
  };

  // Write every filter field + drop `page` in ONE URL patch (atomic).
  const writeFilters = (v: DispatchFilterValue) => {
    patchUrl((params) => {
      const setOrDelete = (key: string, value: string) => {
        if (value) params.set(key, value);
        else params.delete(key);
      };
      setOrDelete('hub', v.hubId);
      setOrDelete('client', v.clientIds.join(','));
      setOrDelete('preset', v.datePreset);
      setOrDelete('from', v.from);
      setOrDelete('to', v.to);
      params.delete('page');
    });
  };

  const applyFilter = () => {
    writeFilters(filterDraft);
    setFilterOpen(false);
  };

  const resetFilter = () => {
    setFilterDraft(EMPTY_DISPATCH_FILTERS);
    writeFilters(EMPTY_DISPATCH_FILTERS);
    setFilterOpen(false);
  };

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-[28px] font-extrabold tracking-tight text-text-strong-950 leading-none">
            Dispatches
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-text-sub-600 leading-relaxed">
            History of dispatch runs — each row is one ops action that batched
            HUB_SCANNED items into rider trips and bridged them to delivery.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button tone="neutral" style="stroke" onClick={fetchList}>
            <i className="ri-refresh-line mr-1.5 text-[14px]" />
            Refresh
          </Button>
          <Button
            tone="neutral"
            style="stroke"
            onClick={() => setExportOpen(true)}
          >
            <i className="ri-download-2-line mr-1.5 text-[14px]" />
            Export dispatch
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <i className="ri-add-line mr-1.5 text-[14px]" />
            Create dispatch
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-stroke-soft-200 bg-white">
        <div className="flex flex-col gap-3 border-b border-stroke-soft-200 px-4 pt-1 pb-3 lg:flex-row lg:items-end lg:justify-between lg:pb-0">
          <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
            <TabsList variant="line" className="w-auto border-0">
              {TABS.map((t) => (
                <TabsTrigger key={t.key} value={t.key}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          <div className="flex flex-1 items-center justify-end gap-2 flex-wrap lg:py-2">
            <DispatchFilterPanel
              open={filterOpen}
              onOpenChange={handleFilterOpenChange}
              activeFilterCount={activeFilterCount}
              value={filterDraft}
              onChange={setFilterDraft}
              onApply={applyFilter}
              onReset={resetFilter}
            />
          </div>
        </div>

        <div className="p-4">
          <TableData<Dispatch>
            data={items}
            columns={columns}
            loading={tableLoading}
            size="sm"
            onRowClick={(record) => navigate(`/dispatches/${record.id}`)}
          />

          {total > 0 && (
            <Paginator
              currentPage={page}
              totalItems={total}
              itemsPerPage={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
            />
          )}
        </div>
      </div>

      <CreateDispatchModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(d) => {
          // The new PREVIEW is the natural next thing ops wants to see —
          // skip the list refresh and jump straight into review/edit.
          navigate(`/dispatches/${d.id}`);
        }}
      />

      <ExportDispatchesModal
        isOpen={exportOpen}
        onClose={() => setExportOpen(false)}
      />
    </div>
  );
};

export default DispatchesPage;
